import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { debitNoteService, debitNoteLineService } from '../services/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Plus, Trash2, Edit2, Printer, FileText, Save, X } from 'lucide-react';
import { useReactToPrint } from 'react-to-print';
import { PrintableDebitNoteSection } from '../components/PrintableDocuments';

const STATUSES = [
  { value: 'bozza', label: 'Bozza', cls: 'bg-slate-100 text-slate-700' },
  { value: 'emessa', label: 'Emessa', cls: 'bg-blue-100 text-blue-800' },
  { value: 'inviata', label: 'Inviata', cls: 'bg-amber-100 text-amber-800' },
  { value: 'chiusa', label: 'Chiusa', cls: 'bg-emerald-100 text-emerald-800' },
];

const LINE_SUGGESTIONS = ['Selezione', 'Trasporto', 'Rilavorazione', 'Rottamazione', 'Fermo produzione', 'Gestione pratica'];

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '-';
const fmtCurrency = (v, cur = 'EUR') => new Intl.NumberFormat('it-IT', { style: 'currency', currency: cur }).format(v || 0);

const statusBadge = (s) => {
  const opt = STATUSES.find(o => o.value === s) || STATUSES[0];
  return <Badge className={opt.cls}>{opt.label}</Badge>;
};

export default function DebitNoteDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const printRef = useRef(null);
  const [dn, setDn] = useState(null);
  const [printData, setPrintData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lineDialogOpen, setLineDialogOpen] = useState(false);
  const [editingLineId, setEditingLineId] = useState(null);
  const [lineForm, setLineForm] = useState({ description: '', quantity: '1', unit_cost: '' });
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await debitNoteService.getById(id);
      setDn(res.data);
    } catch { toast.error('Errore caricamento nota di debito'); } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handlePrint = useReactToPrint({ contentRef: printRef });

  const loadPrintAndPrint = async () => {
    try {
      const res = await debitNoteService.getPrintData(id);
      setPrintData(res.data);
      setTimeout(() => handlePrint(), 300);
    } catch { toast.error('Errore caricamento dati stampa'); }
  };

  const handleSaveLine = async (e) => {
    e.preventDefault();
    if (!lineForm.description || !lineForm.unit_cost) { toast.error('Compilare descrizione e costo'); return; }
    try {
      const payload = {
        description: lineForm.description,
        quantity: parseFloat(lineForm.quantity) || 1,
        unit_cost: parseFloat(lineForm.unit_cost) || 0,
      };
      if (editingLineId) {
        await debitNoteLineService.update(editingLineId, payload);
        toast.success('Riga aggiornata');
      } else {
        await debitNoteLineService.create({ ...payload, debit_note_id: id });
        toast.success('Riga aggiunta');
      }
      setLineDialogOpen(false);
      setEditingLineId(null);
      setLineForm({ description: '', quantity: '1', unit_cost: '' });
      load();
    } catch { toast.error('Errore salvataggio riga'); }
  };

  const handleDeleteLine = async (lineId) => {
    if (!window.confirm('Eliminare questa riga?')) return;
    try { await debitNoteLineService.delete(lineId); toast.success('Riga eliminata'); load(); } catch { toast.error('Errore eliminazione'); }
  };

  const handleStatusChange = async () => {
    try { await debitNoteService.update(id, { status: newStatus }); toast.success('Stato aggiornato'); setStatusDialogOpen(false); load(); } catch { toast.error('Errore aggiornamento stato'); }
  };

  const handleSaveEdit = async () => {
    try { await debitNoteService.update(id, editForm); toast.success('Nota aggiornata'); setEditMode(false); load(); } catch { toast.error('Errore salvataggio'); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Eliminare questa nota di debito?')) return;
    try { await debitNoteService.delete(id); toast.success('Nota eliminata'); nav(-1); } catch { toast.error('Errore eliminazione'); }
  };

  const openEditLine = (line) => {
    setEditingLineId(line.id);
    setLineForm({ description: line.description, quantity: String(line.quantity), unit_cost: String(line.unit_cost) });
    setLineDialogOpen(true);
  };

  const openNewLine = () => {
    setEditingLineId(null);
    setLineForm({ description: '', quantity: '1', unit_cost: '' });
    setLineDialogOpen(true);
  };

  if (loading) return <div className="p-8 text-center text-slate-400">Caricamento...</div>;
  if (!dn) return <div className="p-8 text-center text-slate-400">Nota di debito non trovata</div>;

  const recipientName = dn.recipient_type === 'customer' ? dn.customer_name : dn.supplier_name;

  return (
    <div className="space-y-4" data-testid="debit-note-detail-page">
      {/* Hidden print component */}
      <div className="hidden">
        <PrintableDebitNoteSection ref={printRef} data={printData || dn} />
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button onClick={() => nav(-1)} className="p-1.5 rounded-lg hover:bg-slate-200 text-slate-500" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <FileText className="w-5 h-5 text-slate-400" />
              {dn.debit_note_number}
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">{dn.subject} - {recipientName || '-'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => { setNewStatus(dn.status); setStatusDialogOpen(true); }} data-testid="change-status-btn">
            {statusBadge(dn.status)}
          </Button>
          <Button variant="outline" size="sm" onClick={() => { setEditMode(true); setEditForm({ subject: dn.subject, description: dn.description || '', notes: dn.notes || '' }); }} data-testid="edit-btn">
            <Edit2 className="w-3.5 h-3.5 mr-1" /> Modifica
          </Button>
          <Button variant="outline" size="sm" onClick={loadPrintAndPrint} data-testid="print-btn">
            <Printer className="w-3.5 h-3.5 mr-1" /> Stampa
          </Button>
          <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50 border-red-200" onClick={handleDelete} data-testid="delete-btn">
            <Trash2 className="w-3.5 h-3.5 mr-1" /> Elimina
          </Button>
        </div>
      </div>

      {/* Info Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          {/* Details Card */}
          <Card>
            <CardHeader><CardTitle className="text-base">Dettagli Nota di Debito</CardTitle></CardHeader>
            <CardContent>
              {editMode ? (
                <div className="space-y-3">
                  <div><Label className="text-xs uppercase tracking-wide text-slate-400">Oggetto</Label>
                    <Input value={editForm.subject} onChange={e => setEditForm(f => ({ ...f, subject: e.target.value }))} className="mt-1" data-testid="edit-subject" /></div>
                  <div><Label className="text-xs uppercase tracking-wide text-slate-400">Descrizione</Label>
                    <Textarea value={editForm.description} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} rows={3} className="mt-1" data-testid="edit-description" /></div>
                  <div><Label className="text-xs uppercase tracking-wide text-slate-400">Note</Label>
                    <Textarea value={editForm.notes} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))} rows={2} className="mt-1" data-testid="edit-notes" /></div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={handleSaveEdit} data-testid="save-edit-btn"><Save className="w-3.5 h-3.5 mr-1" />Salva</Button>
                    <Button size="sm" variant="outline" onClick={() => setEditMode(false)}><X className="w-3.5 h-3.5 mr-1" />Annulla</Button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-y-3 gap-x-6 text-sm">
                  <div><span className="text-slate-400 text-xs uppercase tracking-wide block">Tipo Destinatario</span><span className="font-medium">{dn.recipient_type === 'customer' ? 'Cliente' : 'Fornitore'}</span></div>
                  <div><span className="text-slate-400 text-xs uppercase tracking-wide block">Destinatario</span><span className="font-medium">{recipientName || '-'}</span></div>
                  <div><span className="text-slate-400 text-xs uppercase tracking-wide block">Oggetto</span><span className="font-medium">{dn.subject}</span></div>
                  <div><span className="text-slate-400 text-xs uppercase tracking-wide block">Data Emissione</span><span className="font-medium">{fmtDate(dn.issue_date)}</span></div>
                  <div><span className="text-slate-400 text-xs uppercase tracking-wide block">Non Conformità</span><span className="font-mono font-medium">{dn.nc_number || '-'}</span></div>
                  <div><span className="text-slate-400 text-xs uppercase tracking-wide block">Prodotto / Lotto</span><span className="font-medium">{[dn.product_code, dn.lot_code].filter(Boolean).join(' / ') || '-'}</span></div>
                  {dn.description && <div className="col-span-2"><span className="text-slate-400 text-xs uppercase tracking-wide block">Descrizione</span><span>{dn.description}</span></div>}
                  {dn.notes && <div className="col-span-2"><span className="text-slate-400 text-xs uppercase tracking-wide block">Note</span><span>{dn.notes}</span></div>}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Lines Table */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Voci di Costo</CardTitle>
              <Button size="sm" onClick={openNewLine} data-testid="add-line-btn"><Plus className="w-3.5 h-3.5 mr-1" />Aggiungi Voce</Button>
            </CardHeader>
            <CardContent>
              {dn.lines && dn.lines.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead><tr><th>Descrizione</th><th className="w-20 text-center">Qtà</th><th className="w-28 text-right">Costo Unit.</th><th className="w-28 text-right">Totale</th><th className="w-20">Azioni</th></tr></thead>
                    <tbody>
                      {dn.lines.map(line => (
                        <tr key={line.id} className="group hover:bg-slate-50">
                          <td className="font-medium">{line.description}</td>
                          <td className="text-center">{line.quantity}</td>
                          <td className="text-right">{fmtCurrency(line.unit_cost, dn.currency)}</td>
                          <td className="text-right font-semibold">{fmtCurrency(line.total_cost, dn.currency)}</td>
                          <td>
                            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Button size="icon" variant="ghost" className="h-7 w-7 text-blue-600" onClick={() => openEditLine(line)} data-testid={`edit-line-${line.id}`}><Edit2 className="w-3.5 h-3.5" /></Button>
                              <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => handleDeleteLine(line.id)} data-testid={`delete-line-${line.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-slate-900">
                        <td colSpan={3} className="text-right font-bold text-sm py-2">TOTALE</td>
                        <td className="text-right font-bold text-base" data-testid="total-amount">{fmtCurrency(dn.total_amount, dn.currency)}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-slate-400 text-center py-6">Nessuna voce di costo. Aggiungi la prima voce.</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Side Panel */}
        <div className="space-y-4">
          <Card data-testid="summary-card">
            <CardContent className="pt-5">
              <div className="text-center mb-4">
                <p className="text-xs uppercase tracking-wide text-slate-400">Importo Totale</p>
                <p className="text-3xl font-bold text-slate-900 mt-1" data-testid="side-total">{fmtCurrency(dn.total_amount, dn.currency)}</p>
              </div>
              <div className="space-y-2 text-sm border-t pt-3">
                <div className="flex justify-between"><span className="text-slate-400">Stato</span>{statusBadge(dn.status)}</div>
                <div className="flex justify-between"><span className="text-slate-400">Valuta</span><span>{dn.currency}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Creata il</span><span>{fmtDate(dn.created_at)}</span></div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Line Dialog */}
      <Dialog open={lineDialogOpen} onOpenChange={setLineDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingLineId ? 'Modifica Voce' : 'Nuova Voce di Costo'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSaveLine}>
            <div className="space-y-4 py-4">
              <div>
                <Label>Descrizione *</Label>
                <Input value={lineForm.description} onChange={e => setLineForm(f => ({ ...f, description: e.target.value }))} placeholder="es. Selezione, Trasporto..." data-testid="line-description" />
                <div className="flex flex-wrap gap-1 mt-2">
                  {LINE_SUGGESTIONS.map(s => (
                    <button key={s} type="button" onClick={() => setLineForm(f => ({ ...f, description: s }))}
                      className="text-xs px-2 py-0.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors">{s}</button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Quantità</Label><Input type="number" step="any" value={lineForm.quantity} onChange={e => setLineForm(f => ({ ...f, quantity: e.target.value }))} data-testid="line-quantity" /></div>
                <div><Label>Costo Unitario *</Label><Input type="number" step="0.01" value={lineForm.unit_cost} onChange={e => setLineForm(f => ({ ...f, unit_cost: e.target.value }))} data-testid="line-unit-cost" /></div>
              </div>
              {lineForm.quantity && lineForm.unit_cost && (
                <p className="text-sm text-slate-500">Totale: <strong>{fmtCurrency((parseFloat(lineForm.quantity) || 0) * (parseFloat(lineForm.unit_cost) || 0))}</strong></p>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setLineDialogOpen(false)}>Annulla</Button>
              <Button type="submit" data-testid="save-line-btn">{editingLineId ? 'Salva Modifiche' : 'Aggiungi'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Status Dialog */}
      <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Cambia Stato</DialogTitle></DialogHeader>
          <div className="py-4">
            <Select value={newStatus} onValueChange={setNewStatus}>
              <SelectTrigger data-testid="status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusDialogOpen(false)}>Annulla</Button>
            <Button onClick={handleStatusChange} data-testid="confirm-status-btn">Conferma</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
